import { Navigate, useNavigate } from '@tanstack/react-router'
import { AnimatePresence, motion } from 'framer-motion'
import { Loader2 } from 'lucide-react'
import { FormEvent, useState } from 'react'
import styles from './Index.module.css'
import forgotPasswordGroupImage from '@/assets/brand/forgot-password-group-814.svg'
import forgotPasswordSentGroupImage from '@/assets/brand/forgot-password-sent-group-811.svg'
import { AuthSplitLayout } from '@/components/auth/AuthSplitLayout'
import { RouteErrorBoundary } from '@/components/error/RouteErrorBoundary'
import { useAuth } from '@/contexts/AuthContext'
import { useRequestPasswordResetMutation } from '@/hooks/useProfileMutations'
import { getHomeRoute } from '@/lib/routes'

export default function ForgotPassword() {
  const { isAuthenticated, profile, loading: authLoading, authError, loginWithRedirect, logout } = useAuth()
  const requestPasswordResetMutation = useRequestPasswordResetMutation()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [isSent, setIsSent] = useState(false)

  if (authLoading) {
    return (
      <div className="flex justify-center items-center h-dvh" data-testid="auth-loading">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  if (isAuthenticated) {
    if (profile) {
      return <Navigate to={getHomeRoute(profile.role)} replace />
    }

    return (
      <RouteErrorBoundary
        error={new Error(authError || 'We could not finish signing you in. Sign out and try again.')}
        title="Session issue"
        retryLabel="Sign Out"
        onRetry={() => void logout().catch(() => {})}
      />
    )
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    await requestPasswordResetMutation.mutateAsync(email.trim())
    setIsSent(true)
  }

  return (
    <AuthSplitLayout
      heading={
        isSent ? (
          'Your password link has been sent!'
        ) : (
          <>
            <div>Lost your password?</div>
            <div className={styles.mobileOnly}>Enter your details to recover...</div>
          </>
        )
      }
      subtitle={
        isSent
          ? 'Check your inbox and update your password'
          : 'A password reset link will be sent to your inbox.'
      }
      rightImageSrc={isSent ? forgotPasswordSentGroupImage : forgotPasswordGroupImage}
      rightBackground="forgotPassword"
      animateHeadingSubtitle
      subtitleMarginBottom={isSent ? 0 : undefined}
      onGoogleClick={() =>
        loginWithRedirect({
          authorizationParams: { connection: 'google-oauth2' },
        })
      }
    >
      <AnimatePresence mode="wait" initial={false}>
        {isSent ? (
          <motion.div
            key="forgot-sent"
            className={styles.form}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
          >
            <button
              className={`${styles.loginButton} ${styles.sentReturnButton}`}
              type="button"
              onClick={() => navigate({ to: '/' })}
            >
              Return to Log In
            </button>
          </motion.div>
        ) : (
          <motion.form
            key="forgot-form"
            onSubmit={handleSubmit}
            className={styles.form}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
          >
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              className={styles.input}
              placeholder="Email"
              aria-label="Email"
              disabled={requestPasswordResetMutation.isPending}
              required
            />

            <button
              className={`${styles.loginButton} ${styles.forgotSubmitButton}`}
              type="submit"
              disabled={requestPasswordResetMutation.isPending}
            >
              {requestPasswordResetMutation.isPending ? 'Sending...' : 'Submit'}
            </button>
          </motion.form>
        )}
      </AnimatePresence>
    </AuthSplitLayout>
  )
}
